import { FC, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  InputAdornment,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationOnIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  usePremiseHook,
  useDeletePremiseHook,
} from "../../../hooks/useSpocHook";
import {
  TPremise,
  TPremiseStatus,
} from "../../../apis/spocPremise/spocPremise.interfaces";
import { useInstituteContext } from "../../../context/institute.context";
import ManagePremiseDialog from "./managePremiseDialog";
import { logger, pages } from "../../../utils/common";
import { useNavigate } from "react-router-dom";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";

const PremiseListView: FC = () => {
  const navigate = useNavigate();
  const { selectedInstitute } = useInstituteContext();
  const { data, isLoading, error } = usePremiseHook();
  const deletePremiseMutation = useDeletePremiseHook();
  const { control, watch } = useForm({ defaultValues: { search: "" } });
  const searchQuery = watch("search");
  const [statusFilter, setStatusFilter] = useState<TPremiseStatus | "all">(
    "all",
  );
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [editingPremise, setEditingPremise] = useState<TPremise | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [premiseToDelete, setPremiseToDelete] = useState<TPremise | null>(null);

  const filteredPremises = useMemo(() => {
    if (!data) return [];

    return data.filter((premise) => {
      const matchesSearch =
        premise.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        premise.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
        premise.capacity.toString().includes(searchQuery);

      // Filter out archived by default unless explicitly selected
      const matchesStatus =
        statusFilter === "all"
          ? premise.status !== "archived"
          : premise.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [data, searchQuery, statusFilter]);

  const getStatusChip = (status: TPremiseStatus) => {
    const statusConfig = {
      active: { label: "Active", color: "success" as const },
      inactive: { label: "Inactive", color: "default" as const },
      archived: { label: "Archived", color: "error" as const },
    };

    const config = statusConfig[status] || statusConfig.active;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleAddPremise = () => {
    setEditingPremise(null);
    setManageDialogOpen(true);
  };

  const handleEditPremise = (premise: TPremise) => {
    if (premise.status !== "archived") {
      setEditingPremise(premise);
      setManageDialogOpen(true);
    }
  };

  const handleViewConfiguration = (premise: TPremise) => {
    navigate(`${pages.spocPremise.path}/premise/${premise.id}`);
  };

  const handleDeletePremise = (premise: TPremise) => {
    setPremiseToDelete(premise);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!premiseToDelete) return;

    try {
      await deletePremiseMutation.mutateAsync(premiseToDelete.id);
      logger.show_success("Premise deleted successfully!");
      setDeleteDialogOpen(false);
      setPremiseToDelete(null);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to delete premise. Please try again.",
      );
    }
  };

  const handleManageDialogClose = () => {
    setManageDialogOpen(false);
    setEditingPremise(null);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        data-testid="premise-list-view"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} data-testid="premise-list-view">
        <Alert severity="error">
          Failed to load premises. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={3} data-testid="premise-list-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ my: 2 }}
            data-testid="premise-header"
          >
            <Typography variant="h6">Premise Management</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPremise}
              size="large"
              data-testid="add-premise-button"
            >
              Add Premise
            </Button>
          </Box>
        </Grid>

        {/* Search and Filters */}
        <Grid item xs={12}>
          <Card data-testid="premise-filters-card">
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <ControlledTextField
                    control={control}
                    name="search"
                    id="premise-search"
                    label="Search"
                    isGridItem={false}
                    fullWidth
                    placeholder="Search by name, address, or capacity..."
                    inputProps={{ "data-testid": "premise-search-input" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip
                      label="All"
                      onClick={() => setStatusFilter("all")}
                      color={statusFilter === "all" ? "primary" : "default"}
                      variant={statusFilter === "all" ? "filled" : "outlined"}
                      data-testid="premise-status-filter-all"
                    />
                    <Chip
                      label="Active"
                      onClick={() => setStatusFilter("active")}
                      color={statusFilter === "active" ? "primary" : "default"}
                      variant={
                        statusFilter === "active" ? "filled" : "outlined"
                      }
                      data-testid="premise-status-filter-active"
                    />
                    <Chip
                      label="Inactive"
                      onClick={() => setStatusFilter("inactive")}
                      color={
                        statusFilter === "inactive" ? "primary" : "default"
                      }
                      variant={
                        statusFilter === "inactive" ? "filled" : "outlined"
                      }
                      data-testid="premise-status-filter-inactive"
                    />
                    <Chip
                      label="Archived"
                      onClick={() => setStatusFilter("archived")}
                      color={
                        statusFilter === "archived" ? "primary" : "default"
                      }
                      variant={
                        statusFilter === "archived" ? "filled" : "outlined"
                      }
                      data-testid="premise-status-filter-archived"
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Premise List */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Premises ({filteredPremises.length})
          </Typography>
        </Grid>

        {filteredPremises.length === 0 ? (
          <Grid item xs={12}>
            <Card data-testid="premise-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                  gap={2}
                  data-testid="premise-empty-content"
                >
                  <LocationOnIcon
                    sx={{ fontSize: 64, color: "text.secondary" }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    {data?.length === 0
                      ? "No premises configured"
                      : "No premises match your search criteria"}
                  </Typography>
                  {data?.length === 0 && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Get started by adding your first premise.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddPremise}
                        data-testid="add-premise-empty-button"
                      >
                        Add Premise
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <TableContainer
              component={Paper}
              variant="outlined"
              data-testid="premise-table-container"
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Capacity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right" sx={{ minWidth: 150 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPremises.map((premise) => (
                    <TableRow
                      key={premise.id}
                      hover
                      data-testid={`premise-row-${premise.id}`}
                    >
                      <TableCell>
                        <Typography variant="body2">{premise.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {premise.address}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {premise.capacity.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(premise.status)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleViewConfiguration(premise)}
                          color="primary"
                          aria-label="configure"
                          title="Configure"
                          data-testid={`configure-premise-${premise.id}`}
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                        {premise.status !== "archived" && (
                          <IconButton
                            size="small"
                            onClick={() => handleEditPremise(premise)}
                            color="primary"
                            aria-label="edit"
                            title="Edit"
                            data-testid={`edit-premise-${premise.id}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {premise.status === "archived" && (
                          <IconButton
                            size="small"
                            disabled
                            aria-label="edit disabled"
                            title="Cannot edit archived premise"
                            data-testid={`edit-premise-disabled-${premise.id}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleDeletePremise(premise)}
                          color="error"
                          aria-label="delete"
                          title="Delete"
                          data-testid={`delete-premise-${premise.id}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setPremiseToDelete(null);
        }}
        data-testid="delete-premise-dialog"
      >
        <DialogTitle>Delete Premise</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete premise "{premiseToDelete?.name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setPremiseToDelete(null);
            }}
            disabled={deletePremiseMutation.isPending}
            data-testid="cancel-delete-premise-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deletePremiseMutation.isPending}
            startIcon={
              deletePremiseMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            data-testid="confirm-delete-premise-button"
          >
            {deletePremiseMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <ManagePremiseDialog
        open={manageDialogOpen}
        onClose={handleManageDialogClose}
        premise={editingPremise}
      />
    </>
  );
};

export default PremiseListView;
